import { Subscription } from 'rxjs';
import { FieldType, PropertyPreviewMetadata, ReportPreviewMetadata, UpdateMetadata } from '../_services/report-template-design.service';
import { BORDER_STYLE, DYNAMIC_SELECT_ITEM, FONT_FAMILY, FONT_STYLE, FONT_WEIGHT, INPUT_TYPES } from '../app.constants';
import { ElementType } from './element-type';
import { ReportElement } from './report-element';
import { ReportFonts } from './report-fonts';
import { ElementLocationInSection, TemplateText } from './report-template';
import { ReportUnits } from './report-units';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../_services/lang-update.service';

export class ReportText extends ReportElement {
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public padding: string;
  public paddingLeft: string;
  public paddingBottom: string;
  public paddingRight: string;
  public paddingTop: string;
  public zIndex: number;
  public borderWidth: string;
  public borderStyle: string;
  public borderColor: string;
  public backgroundColor: string;
  public text: string;
  public fontSize: string;
  public fontFamily: string;
  public fontStyle: string;
  public fontWeight: string;
  public color: string;
  public textAlign: string;
  public verticalAlign: string;
  public isHorizontal: boolean;
  public lineHeight: string;
  public justifyContent: string;
  public alignItems: string;
  public editMode?: boolean;
  public isCustomElementDraw?: boolean;
  public customTop?: string;
  public previewLabel: string;

  private units: ReportUnits;
  private fonts: ReportFonts;

  private baseDef: TemplateText;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaBGColor = 'backgroundColor';
  private readonly metaFontSize = 'fontSize';
  private readonly metaFontFamily = 'fontFamily';
  private readonly metaFontStyle = 'fontStyle';
  private readonly metaFontWeight = 'fontWeight';
  private readonly metaFontColor = 'fontColor';
  private readonly metaTextAlign = 'horizontalAlign';
  private readonly metaVerticalAlign = 'verticalAlign';
  private readonly metaIsHorizontal = 'isHorizontal';
  private readonly metaLineHeight = 'lineHeight';
  private readonly metaText = 'text';

  private readonly metaType = 'type';
  private readonly metaDynamicDataField = 'dynamicDataField';
  private readonly metaDynamicId = 'dynamicId';
  private readonly metaEditableId = 'editableId';
  private readonly metaInputType = 'inputType';
  private readonly metaValidation = 'validation';
  private readonly metaFormat = 'format';
  private readonly metaRequired = 'required';
  private readonly metaMaxLength = 'maxLength';
  private readonly metaRadioType = 'radioType';
  private readonly metaRadioLabel = 'radioLabel';
  private readonly metaOptions = 'options';
  private readonly metaLabel = 'label';
  private readonly metaValue = 'value';
  private readonly metaInputGroup = 'inputGroup';
  private readonly metaPaddingLeft = 'paddingLeft';
  private readonly metaPaddingBottom = 'paddingBottom';
  private readonly metaPaddingRight = 'paddingRight';
  private readonly metaPaddingTop = 'paddingTop';
  private readonly metaMultiSelectDelimiter = 'multiSelectDelimiter';

  /**
   * コンストラクタ
   */
  constructor(index: number, units: ReportUnits, fonts: ReportFonts,
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    templateText?: TemplateText) {

    super(ElementType.Text, index, !(templateText == null), translate, langService, templateText ? templateText.id : null);

    this.units = units;
    this.fonts = fonts;

    if (templateText) {
      this.baseDef = templateText;
      this.baseDef.id = this.baseDef.id ? this.baseDef.id : this.generateUUID();
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: null,
        y: null,
        width: null,
        height: null,
        zIndex: null,
        borderColor: null,
        borderStyle: null,
        borderWidth: null,
        backgroundColor: 'transparent',
        type: 'static',
        text: '',
        format: null,
        fontSize: this.fonts.getInitailFontSize(),
        fontFamily: this.fonts.getInitialFontFamily(),
        fontColor: null,
        fontStyle: null,
        fontWeight: null,
        horizontalAlign: 'left',
        verticalAlign: 'middle',
        isHorizontal: true,
        lineHeight: null,
        dynamicDataField: null,
        dynamicId: null,
        inputType: 'text',
        editableId: null,
        validation: {
          required: false,
          maxLength: 100,
        },
        radioType: null,
        radioLabel: null,
        options: [],
        label: null,
        value: null,
        inputGroup: null,
        padding: null,
        paddingTop: 0.5,
        paddingLeft: 0.5,
        paddingBottom: 0.5,
        paddingRight: 0.5,
        multiSelectDelimiter: 'comma'
      };
    }

    this.zIndex = this.baseDef.zIndex;
    this.borderColor = this.baseDef.borderColor;
    this.borderStyle = this.baseDef.borderStyle;
    this.borderWidth = this.units.concatenate('sizeUnit', this.baseDef.borderWidth);
    this.backgroundColor = this.baseDef.backgroundColor;
    this.fontStyle = this.baseDef.fontStyle;
    this.fontWeight = this.baseDef.fontWeight;
    this.color = this.baseDef.fontColor;
    this.textAlign = this.baseDef.horizontalAlign;
    this.verticalAlign = this.baseDef.verticalAlign;
    this.isHorizontal = this.baseDef.isHorizontal;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);
    this.width = this.getWidth();
    this.height = this.getHeight();
    this.lineHeight = this.getLineHeight();
    this.fontSize = this.fonts.getFontSize(this.baseDef);
    this.fontFamily = this.fonts.getFontFamily(this.baseDef);

    this.paddingLeft = this.units.concatenate('sizeUnit', this.baseDef.paddingLeft);
    this.paddingBottom = this.units.concatenate('sizeUnit', this.baseDef.paddingBottom);
    this.paddingRight = this.units.concatenate('sizeUnit', this.baseDef.paddingRight);
    this.paddingTop = this.units.concatenate('sizeUnit', this.baseDef.paddingTop);

    this.text = this.getText();
    this.previewLabel = this.getPreviewLabel();
    this.justifyContent = this.getJustifyContent();
    this.alignItems = this.getAlignItems();

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public isGuideMarkType(type: 'editable' | 'static' | 'dynamic'): boolean {
    let rtn = false;
    switch (type) {
      case 'editable':
        rtn = (this.baseDef.type === 'dynamic-editable');
        break;
      case 'static':
        rtn = (this.baseDef.type === 'static');
        break;
      case 'dynamic':
        rtn = (this.baseDef.type === 'dynamic');
        break;
    }
    // console.log('ReportText(%s) isGuideMarkType End.', this.getId());
    return rtn;
  }

  public setDrawData(metadata: ReportPreviewMetadata) {

    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.getValue(metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.getValue(metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }

  public getHeightPx(): number {
    if (this.baseDef[this.metaHeight] !== null && this.baseDef[this.metaHeight] < 0) {
      return this.units.toPx('sizeUnit', 1);
    } else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
    }
  }
  public getWidthPx(): number {
    if (this.baseDef[this.metaWidth] !== null && this.baseDef[this.metaWidth] < 0) {
      return this.units.toPx('sizeUnit', 1);
    } else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
    }
  }

  public getTemplateJSON(): TemplateText {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size'
      },
      {
        name: this.metaWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size'
      },
      {
        name: this.metaPaddingLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaPaddingBottom, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaPaddingRight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaPaddingTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaVerticalAlign, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'top', value: 'top' },
          { label: 'middle', value: 'middle' },
          { label: 'bottom', value: 'bottom' },
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaTextAlign, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'left', value: 'left' },
          { label: 'center', value: 'center' },
          { label: 'right', value: 'right' },
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaLineHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        // ↑ CSSプロパティ「lineHight」は文字列値も受け付けられたような。。。
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaIsHorizontal, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox'
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBorderWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBGColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Background'
      },
      {
        name: this.metaFontSize, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font'
      },
      {
        name: this.metaFontFamily, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_FAMILY,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font'
      },
      {
        name: this.metaFontColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font'
      },
      {
        name: this.metaFontStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font'
      },
      {
        name: this.metaFontWeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_WEIGHT,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font'
      },
      {
        name: this.metaType, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
          { label: 'dynamic-editable', value: 'dynamic-editable' },
        ],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaInputType, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: INPUT_TYPES,
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaText, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'TextArea',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaDynamicId, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: DYNAMIC_SELECT_ITEM,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaDynamicDataField, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: DYNAMIC_SELECT_ITEM,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaFormat, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaMaxLength, parentName: this.metaValidation, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaRequired, parentName: this.metaValidation, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      },
      {
        name: this.metaOptions, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Multi',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Select'
      },
      {
        name: this.metaMultiSelectDelimiter, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'comma', value: 'comma' },
          { label: 'new line', value: 'newline' }
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Select'
      },
      {
        name: this.metaRadioType, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'check', value: 'check' },
          { label: 'circle', value: 'circle' },
          { label: 'disc', value: 'disc' },
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Radio'
      },
      {
        name: this.metaRadioLabel, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Radio'
      },
      {
        name: this.metaValue, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Radio'
      },
      {
        name: this.metaEditableId, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Input'
      },
      {
        name: this.metaLabel, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Input'
      },
      {
        name: this.metaInputGroup, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Input'
      }
    ];
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.getWidth();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.borderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBGColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.backgroundColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.previewLabel = this.getPreviewLabel();
        this.setPreviewValue(metadata.name, metadata.value);
        if (metadata.value === 'static') {
          this.setPreviewValue(this.metaDynamicId, null);
          this.baseDef[this.metaDynamicId] = null;
          this.setPreviewValue(this.metaEditableId, null);
          this.baseDef[this.metaEditableId] = null;
          this.setPreviewValue(this.metaFormat, null);
          this.baseDef[this.metaFormat] = null;
        } else if (metadata.value === 'dynamic') {
          this.setPreviewValue(this.metaText, '');
          this.baseDef[this.metaText] = '';
          this.setPreviewValue(this.metaEditableId, null);
          this.baseDef[this.metaEditableId] = null;
        }
        break;
      case this.metaText:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.previewLabel = this.getPreviewLabel();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFormat:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.fontSize = this.fonts.getFontSize(this.baseDef);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontFamily:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontFamily = this.fonts.getFontFamily(this.baseDef);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.color = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontWeight:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontWeight = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTextAlign:
        this.baseDef[metadata.name] = metadata.value as string;
        this.textAlign = metadata.value as string;
        this.justifyContent = this.getJustifyContent();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaVerticalAlign:
        this.baseDef[metadata.name] = metadata.value as string;
        this.verticalAlign = metadata.value as string;
        this.alignItems = this.getAlignItems();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaIsHorizontal:
        this.baseDef[metadata.name] = metadata.value as boolean;
        this.isHorizontal = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLineHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.lineHeight = this.getLineHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaDynamicId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.previewLabel = this.getPreviewLabel();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaDynamicDataField:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.previewLabel = this.getPreviewLabel();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaInputType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        if (metadata.value !== 'select' && metadata.value !== 'multiSelect') {
          this.setPreviewValue(this.metaOptions, '[]');
          this.baseDef[this.metaOptions] = [];
        }
        if (metadata.value !== 'multiSelect') {
          this.setPreviewValue(this.metaMultiSelectDelimiter, 'comma');
          this.baseDef[this.metaMultiSelectDelimiter] = 'comma';
        }
        if (metadata.value !== 'radio') {
          this.setPreviewValue(this.metaRadioType, null);
          this.baseDef[this.metaRadioType] = null;
          this.setPreviewValue(this.metaRadioLabel, null);
          this.baseDef[this.metaRadioLabel] = null;
          this.setPreviewValue(this.metaValue, null);
          this.baseDef[this.metaValue] = null;
        }
        break;
      case this.metaEditableId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.previewLabel = this.getPreviewLabel();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRequired:
        //if (!this.baseDef[this.metaValidation]) {
        //  this.baseDef[this.metaValidation] = { required: false, maxLength: null };
        //}
        this.baseDef[this.metaValidation][metadata.name] = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaMaxLength:
        //if (!this.baseDef[this.metaValidation]) {
        //  this.baseDef[this.metaValidation] = { required: false, maxLength: null };
        //}
        this.baseDef[this.metaValidation][metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaRadioType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRadioLabel:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaOptions:
        this.baseDef[metadata.name] = JSON.parse(metadata.value as string);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaMultiSelectDelimiter:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLabel:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaValue:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaInputGroup:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaPaddingLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.paddingLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaPaddingBottom:
        this.baseDef[metadata.name] = metadata.value as number;
        this.paddingBottom = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaPaddingRight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.paddingRight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaPaddingTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.paddingTop = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaMultiSelectDelimiter:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {

    subscribers.push(
      this.units.onUpdateFontSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaFontSize,
          value: this.convertUnit(this.baseDef[this.metaFontSize], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef.fontSize
        });
      })
    );

    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLineHeight,
          value: this.convertUnit(this.baseDef[this.metaLineHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLineHeight]
        });
      })
    );

    subscribers.push(
      this.fonts.onUpdateFontSize.subscribe((metadata: UpdateMetadata) => {
        this.fontSize = this.fonts.getFontSize(this.baseDef);
      })
    );

    subscribers.push(
      this.fonts.onUpdateFontFamily.subscribe((metadata: UpdateMetadata) => {
        this.fontFamily = this.fonts.getFontFamily(this.baseDef);
      })
    );

  }

  private getText(): string {
    let rtnText = '';
    switch (this.baseDef.type) {
      case 'static':
        rtnText = this.baseDef.text;
        break;
      case 'dynamic':
        if (this.baseDef.dynamicDataField) {
          rtnText = '[' + this.translate.instant('objects.' + this.baseDef.dynamicDataField) + ']';
        } else if (this.baseDef.dynamicId) {
          rtnText = '[' + this.translate.instant('objects.' + this.baseDef.dynamicId) + ']';
        } else {
          rtnText = '[' + this.translate.instant('objects.dynamicText') + ']';
        }
        break;
      case 'dynamic-editable':
        rtnText = this.baseDef.editableId ? '{' + this.baseDef.editableId + '}' : '{' + this.translate.instant('objects.editableText') + '}';
        break;
    }
    return rtnText;
  }

  private getPreviewLabel(): string {
    let previewLabel = '';
    DYNAMIC_SELECT_ITEM
    switch (this.baseDef.type) {
      case 'static':
        previewLabel = this.baseDef.text;
        break;
      case 'dynamic':
        if (this.baseDef.dynamicDataField) {
          const label = DYNAMIC_SELECT_ITEM.find(_ => _.value === this.baseDef.dynamicDataField)?.label;
          previewLabel = '[' + this.translate.instant('objects.' + (label ? label : this.baseDef.dynamicDataField)) + ']';
        } else if (this.baseDef.dynamicId) {
          const label = DYNAMIC_SELECT_ITEM.find(_ => _.value === this.baseDef.dynamicId)?.label;
          previewLabel = '[' + this.translate.instant('objects.' + (label ? label : this.baseDef.dynamicId)) + ']';
        } else {
          previewLabel = '[' + this.translate.instant('objects.dynamicText') + ']';
        }
        break;
      case 'dynamic-editable':
        previewLabel = this.baseDef.editableId ? '{' + this.baseDef.editableId + '}' : '{' + this.translate.instant('objects.editableText') + '}';
        break;
    }
    return previewLabel
  }

  private getJustifyContent(): string {
    let rtn: string;
    switch (this.baseDef.horizontalAlign) {
      case 'left':
        rtn = 'flex-start';
        break;
      case 'center':
        rtn = 'center';
        break;
      case 'right':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

  private getAlignItems(): string {
    let rtn: string;
    switch (this.baseDef.verticalAlign) {
      case 'top':
        rtn = 'flex-start';
        break;
      case 'middle':
        rtn = 'center';
        break;
      case 'bottom':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }
  private getLineHeight(): string {
    if (
      this.baseDef.lineHeight !== null &&
      this.baseDef.lineHeight !== undefined &&
      this.baseDef.lineHeight > 0
    ) {
      return this.units.concatenate('sizeUnit', this.baseDef.lineHeight);
    } else {
      return 'initial';
    }
  }
  private getHeight(): string {
    if (
      this.baseDef.height !== null &&
      this.baseDef.height !== undefined &&
      this.baseDef.height > 0
    ) {
      return this.units.concatenate('sizeUnit', this.baseDef.height);
    } else {
      return this.units.concatenate('sizeUnit', 1);
    }
  }
  private getWidth(): string {
    if (
      this.baseDef.width !== null &&
      this.baseDef.width !== undefined &&
      this.baseDef.width > 0
    ) {
      return this.units.concatenate('sizeUnit', this.baseDef.width);
    } else {
      return this.units.concatenate('sizeUnit', 1);
    }
  }
  private getValue(value: number): number {
    if (value !== null && value > 0) {
      return this.units.fromPx('sizeUnit', value);
    } else {
      return this.units.fromPx('sizeUnit', 1);
    }
  }

  public getMetadataId() {
    return this.baseDef[this.metaId];
  }

  public isCustomElement() {
    return this.baseDef['isCustomElementDraw'];
  }
}
